import axios from 'axios';
import {
  getRefreshToken,
  setAccessToken,
  setRefreshToken,
  setTokenExpiryTime,
  tokenTypes
} from 'utils/AuthUtils/tokenUtils';

let isRefreshing = false;
let failedQueue: Array<{ resolve: (token: string) => void; reject: (err: unknown) => void }> = [];

function processQueue(error: unknown, token: string | null = null) {
  failedQueue.forEach(p => (error ? p.reject(error) : p.resolve(token!)));
  failedQueue = [];
}

let apiBaseUrl;
if (import.meta.env.VITE_MODE == 'dev') {
  apiBaseUrl = import.meta.env.VITE_BACKEND_ENDPOINT;
} else {
  apiBaseUrl = window.config.apiBaseUrl;
}

const axiosClient = axios.create({
  baseURL: apiBaseUrl,
  withCredentials: true
});

// function to get Cookie
export function getCookie(name: string) {
  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const [key, value] = cookie.trim().split('=');
    if (key === name) {
      return value;
    }
  }
  return null;
}

// get the access token from localStorage and add it to the axios config header
axiosClient.interceptors.request.use(
  config => {
    const token = localStorage.getItem(tokenTypes.access) || '';
    const csrfToken = getCookie('csrftoken');

    if (csrfToken) {
      config.headers['X-CSRFToken'] = csrfToken;
    }
    config.headers = config.headers ?? {};

    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }

    // Add database query param if TEST_MODE is enabled
    // Set USE_TEST_MODE to true to always use test database, false to use default database
    const USE_TEST_MODE = import.meta.env.VITE_TEST_MODE === 'true';
  

    let testMode = true;
    if (USE_TEST_MODE) {
      testMode = true;
    } else {
      testMode = false;
      // When USE_TEST_MODE is false, testMode remains false regardless of other environment variables
    }

    if (testMode) {
      const url = config.url || '';
      const separator = url.includes('?') ? '&' : '?';
      config.url = `${url}${separator}database=test&frontend_origin=http://localhost:3000`;
    }

    return config;
  },
  error => Promise.reject(error)
);

axiosClient.interceptors.response.use(
  response => response,
  async error => {
    const originalRequest = error.config;

    if (error?.response?.status === 401 && !originalRequest._retry) {
      const storedRefreshToken = getRefreshToken();

      if (!storedRefreshToken) {
        setAccessToken('');
        setTokenExpiryTime('');
        window.location.href = '/';
        return Promise.reject(error);
      }

      if (isRefreshing) {
        return new Promise<string>((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(token => {
            originalRequest.headers['Authorization'] = `Bearer ${token}`;
            return axiosClient(originalRequest);
          })
          .catch(err => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const { data } = await axios.post(
          `${apiBaseUrl}/auth/refresh`,
          { refresh: storedRefreshToken },
          { withCredentials: true }
        );
        const newAccessToken: string = data.access;
        setAccessToken(newAccessToken);

        try {
          const payload = JSON.parse(atob(newAccessToken.split('.')[1]));
          setTokenExpiryTime(new Date(payload.exp * 1000).toUTCString());
        } catch {
          // keep existing expiry if decode fails
        }

        processQueue(null, newAccessToken);
        originalRequest.headers['Authorization'] = `Bearer ${newAccessToken}`;
        return axiosClient(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        setAccessToken('');
        setRefreshToken('');
        setTokenExpiryTime('');
        window.location.href = '/';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export { axiosClient };
